<template>
  <div class="planning-manager">
    <!-- Header -->
    <div class="header">
      <div class="view-tabs">
        <button 
          v-for="view in views" 
          :key="view"
          @click="currentView = view"
          :class="{ active: currentView === view }"
        >
          {{ view }}
        </button>
      </div>
      <div class="header-info">
        <div class="rdv-count">
          📅 {{ rdvs.length }} RDV
        </div>
        <div class="date-nav">
          <button @click="previousPeriod" :disabled="loading">←</button>
          <span v-if="currentView !== 'Jour'">{{ currentPeriodLabel }}</span>
          <div v-if="currentView === 'Jour'" class="day-date-picker">
            <input 
              type="date" 
              :value="formatDateForInput(currentDate)"
              @change="onDateChange"
              class="date-input"
            />
            <span class="current-day-label">{{ formatDayTitle(currentDate) }}</span>
          </div>
          <button @click="nextPeriod" :disabled="loading">→</button>
        </div>
        <button @click="loadRdvs" :disabled="loading" class="refresh-btn">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
          {{ loading ? 'Chargement...' : 'Actualiser' }}
        </button>
      </div>
    </div>

    <!-- Calendar Grid -->
    <div class="calendar-grid">
      <!-- Vue Jour -->
      <div v-if="currentView === 'Jour'" class="day-view">
        <!-- En-tête du jour -->
        <div class="day-header-container">
          <div class="day-title">
            <h3>{{ formatDayTitle(currentDate) }}</h3>
            <p class="day-subtitle">{{ formatDaySubtitle(currentDate) }}</p>
          </div>
          <div class="day-stats">
            <div class="stat-item">
              <span class="stat-number">{{ getDayRdvCount(currentDate) }}</span>
              <span class="stat-label">RDV</span>
            </div>
            <button @click="goToToday" class="today-btn" title="Aller à aujourd'hui">
              <i class="fas fa-calendar-day"></i>
              Aujourd'hui
            </button>
          </div>
        </div>
        
        <!-- Grille horaire du jour -->
        <div class="day-schedule">
          <div v-for="hour in hours" :key="hour" class="hour-slot">
            <div class="hour-label">
              <span class="hour-time">{{ String(hour).padStart(2, '0') }}:00</span>
            </div>
            <div class="hour-content" @click="createRdv">
              <!-- RDV pour cette heure -->
              <div 
                v-for="rdv in getDayHourRdvs(currentDate, hour)" 
                :key="rdv._id" 
                class="day-rdv-item"
                :class="getRegionClass(rdv.prospect?.client?.adresse?.ville)"
                @click.stop="openRdvModal(rdv)"
              >
                <div class="rdv-time-badge">
                  {{ rdv.heureDebut }} - {{ rdv.heureFin }}
                </div>
                <div class="rdv-main-info">
                  <div class="rdv-client-name">
                    <i class="fas fa-user"></i>
                    {{ rdv.prospect?.client?.nomComplet }}
                  </div>
                  <div class="rdv-technicien-name">
                    <i class="fas fa-wrench"></i>
                    {{ rdv.technicien?.nom }} {{ rdv.technicien?.prenom }}
                  </div>
                </div>
                <div class="rdv-details">
                  <div class="rdv-intervention">
                    <i class="fas fa-tools"></i>
                    {{ rdv.prospect?.typeIntervention }}
                  </div>
                  <div class="rdv-location" v-if="rdv.prospect?.client?.adresse">
                    <i class="fas fa-map-marker-alt"></i>
                    {{ rdv.prospect.client.adresse.ville }}
                  </div>
                </div>
                <div class="rdv-status-priority">
                  <span class="mini-status-badge" :class="getStatusClass(rdv.statut)">
                    {{ rdv.statut }}
                  </span>
                  <span class="mini-priority-badge" :class="getPriorityClass(rdv.prospect?.priorite)">
                    {{ rdv.prospect?.priorite }}
                  </span>
                </div>
              </div>
              
              <!-- Placeholder si pas de RDV -->
              <div v-if="getDayHourRdvs(currentDate, hour).length === 0" class="empty-hour-slot">
                <span class="empty-text">Disponible</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="currentView === 'Semaine'" class="week-view">
        <!-- En-tête du calendrier avec les jours -->
        <div class="calendar-header">
          <div class="time-column-header">Heure</div>
          <div v-for="(day, index) in weekDays" :key="day" class="day-header">
            <div class="day-name">{{ day }}</div>
            <div class="day-date">{{ getWeekDayDate(index) }}</div>
          </div>
        </div>
        
        <!-- Corps du calendrier avec la grille -->
        <div class="calendar-body">
          <div v-for="hour in hours" :key="hour" class="time-row">
            <!-- Colonne des heures -->
            <div class="time-column">
              <div class="time-label">{{ String(hour).padStart(2, '0') }}:00</div>
            </div>
            
            <!-- Cellules pour chaque jour -->
            <div v-for="dayIndex in 7" :key="dayIndex" class="day-column">
              <div class="time-cell" @click="createRdv">
                <!-- RDV pour cette heure et ce jour -->
                <div 
                  v-for="rdv in getRdvs(dayIndex, hour)" 
                  :key="rdv._id" 
                  class="rdv-item" 
                  :class="getRegionClass(rdv.prospect?.client?.adresse?.ville)"
                  @click.stop="openRdvModal(rdv)"
                  :title="`Cliquez pour voir les détails`"
                >
                  <div class="rdv-client">👤 {{ rdv.prospect?.client?.nomComplet || 'Client inconnu' }}</div>
                  <div class="rdv-tech">🔧 {{ rdv.technicien?.nom || 'Technicien' }}</div>
                  <div class="rdv-ville" v-if="rdv.prospect?.client?.adresse?.ville">📍 {{ rdv.prospect.client.adresse.ville }}</div>
                  <div class="rdv-time">⏰ {{ rdv.heureDebut }}-{{ rdv.heureFin }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="currentView === 'Mois'" class="month-view">
        <!-- En-tête des jours de la semaine -->
        <div class="month-header">
          <div v-for="day in ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']" :key="day" class="month-day-header">
            {{ day }}
          </div>
        </div>
        <div class="month-grid">
          <!-- Jours vides avant le premier jour du mois -->
          <div 
            v-for="empty in getFirstDayOffset()" 
            :key="'empty-' + empty" 
            class="day-cell empty-cell"
          ></div>
          <!-- Jours du mois -->
          <div 
            v-for="day in getDaysInMonth()" 
            :key="day" 
            class="day-cell"
            :class="{ 
              'today': isToday(day),
              'has-rdv': getDayRdvs(day).length > 0 
            }"
            @click="selectMonthDay(day)"
          >
            <div class="day-number">{{ day }}</div>
            <div class="day-rdvs">
              <div 
                v-for="rdv in getDayRdvs(day).slice(0, 3)" 
                :key="rdv._id" 
                class="rdv-dot" 
                :class="getStatusClass(rdv.statut)"
                :title="`${rdv.prospect?.client?.nomComplet || 'Client'} - ${rdv.heureDebut}`"
                @click.stop="openRdvModal(rdv)"
              ></div>
              <div v-if="getDayRdvs(day).length > 3" class="more-rdvs">
                +{{ getDayRdvs(day).length - 3 }}
              </div>
            </div>
          </div>
        </div>
        <!-- Liste des RDV du jour sélectionné -->
        <div v-if="selectedMonthDay && getDayRdvs(selectedMonthDay).length > 0" class="month-day-details">
          <h4>📅 RDV du {{ selectedMonthDay }} {{ currentPeriodLabel }}</h4>
          <div class="month-rdv-list">
            <div 
              v-for="rdv in getDayRdvs(selectedMonthDay)" 
              :key="rdv._id" 
              class="month-rdv-item"
              @click="openRdvModal(rdv)"
            >
              <div class="rdv-time">{{ rdv.heureDebut }} - {{ rdv.heureFin }}</div>
              <div class="rdv-info">
                <span class="rdv-client">{{ rdv.prospect?.client?.nomComplet }}</span>
                <span class="rdv-tech">{{ rdv.technicien?.nom }} {{ rdv.technicien?.prenom }}</span>
              </div>
              <span class="status-badge mini" :class="getStatusClass(rdv.statut)">
                {{ rdv.statut }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Vue Tableau -->
      <div v-if="currentView === 'Tableau'" class="table-view">
        <!-- Vue Cartes Mobile -->
        <div class="cards-view-mobile">
          <div v-if="loading" class="loading-cards">
            <i class="fas fa-spinner fa-spin"></i>
            <span>Chargement des rendez-vous...</span>
          </div>
          <div v-else-if="rdvs.length === 0" class="empty-cards">
            <i class="fas fa-calendar-times"></i>
            <p>Aucun rendez-vous trouvé</p>
          </div>
          <div v-else class="rdv-cards-grid">
            <div 
              v-for="rdv in sortedRdvs" 
              :key="rdv._id" 
              class="rdv-card"
              @click="openRdvModal(rdv)"
            >
              <div class="rdv-card-header">
                <div class="rdv-card-date">
                  <i class="fas fa-calendar"></i>
                  {{ formatTableDate(rdv.dateRdv) }}
                </div>
                <span class="status-badge" :class="getStatusClass(rdv.statut)">
                  {{ getStatusIcon(rdv.statut) }} {{ rdv.statut }}
                </span>
              </div>
              
              <div class="rdv-card-body">
                <div class="rdv-card-time">
                  <i class="fas fa-clock"></i>
                  {{ rdv.heureDebut }} - {{ rdv.heureFin }}
                  <span class="duration-badge">{{ rdv.dureeEstimee }}h</span>
                </div>
                
                <div class="rdv-card-client">
                  <i class="fas fa-user"></i>
                  <div class="client-details">
                    <span class="client-name">{{ rdv.prospect?.client?.nomComplet }}</span>
                    <span class="client-address" v-if="rdv.prospect?.client?.adresse">
                      {{ rdv.prospect.client.adresse.ville }}
                    </span>
                  </div>
                </div>
                
                <div class="rdv-card-contact" v-if="rdv.prospect?.client?.telephone">
                  <a :href="`tel:${rdv.prospect.client.telephone}`" class="contact-link-card" @click.stop>
                    <i class="fas fa-phone"></i>
                    {{ rdv.prospect.client.telephone }}
                  </a>
                </div>
                
                <div class="rdv-card-technicien" v-if="rdv.technicien">
                  <i class="fas fa-wrench"></i>
                  <span>{{ rdv.technicien.nom }} {{ rdv.technicien.prenom }}</span>
                </div>
                
                <div class="rdv-card-intervention">
                  <i class="fas fa-tools"></i>
                  <span>{{ rdv.prospect?.typeIntervention }}</span>
                </div>
              </div>
              
              <div class="rdv-card-footer">
                <span class="priority-badge" :class="getPriorityClass(rdv.prospect?.priorite)">
                  {{ getPriorityIcon(rdv.prospect?.priorite) }} {{ rdv.prospect?.priorite }}
                </span>
                <button class="view-details-btn" @click.stop="openRdvModal(rdv)">
                  <i class="fas fa-eye"></i>
                  Détails
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Vue Tableau Desktop -->
        <div class="table-container">
          <table class="rdv-table">
            <thead>
              <tr>
                <th>📅 Date</th>
                <th>⏰ Horaires</th>
                <th>👤 Client</th>
                <th>📞 Contact</th>
                <th>💼 Commercial</th>
                <th>🔧 Technicien</th>
                <th>🛠️ Intervention</th>
                <th>📊 Priorité</th>
                <th>📍 Statut</th>
                <th>🔧 Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading" class="loading-row">
                <td colspan="10" class="loading-cell">
                  <div class="loading-content">
                    <i class="fas fa-spinner fa-spin"></i>
                    Chargement des rendez-vous...
                  </div>
                </td>
              </tr>
              <tr v-else-if="rdvs.length === 0" class="empty-row">
                <td colspan="10" class="empty-cell">
                  <div class="empty-content">
                    <i class="fas fa-calendar-times"></i>
                    <p>Aucun rendez-vous trouvé</p>
                    <small>Les rendez-vous apparaîtront ici une fois créés</small>
                  </div>
                </td>
              </tr>
              <tr 
                v-else
                v-for="rdv in sortedRdvs" 
                :key="rdv._id" 
                class="rdv-row"
                @click="openRdvModal(rdv)"
              >
                <td class="date-cell">
                  <div class="date-info">
                    <div class="date-main">{{ formatTableDate(rdv.dateRdv) }}</div>
                    <div class="date-day">{{ formatDayName(rdv.dateRdv) }}</div>
                  </div>
                </td>
                <td class="time-cell">
                  <div class="time-info">
                    <div class="time-range">{{ rdv.heureDebut }} - {{ rdv.heureFin }}</div>
                    <div class="duration">{{ rdv.dureeEstimee }}h</div>
                  </div>
                </td>
                <td class="client-cell">
                  <div class="client-info">
                    <div class="client-name">{{ rdv.prospect?.client?.nomComplet }}</div>
                    <div class="client-address" v-if="rdv.prospect?.client?.adresse">
                      {{ rdv.prospect.client.adresse.ville }}
                    </div>
                  </div>
                </td>
                <td class="contact-cell">
                  <div class="contact-info">
                    <a 
                      v-if="rdv.prospect?.client?.telephone" 
                      :href="`tel:${rdv.prospect.client.telephone}`" 
                      class="contact-link phone-link"
                      @click.stop
                    >
                      <i class="fas fa-phone"></i>
                      {{ rdv.prospect.client.telephone }}
                    </a>
                    <a 
                      v-if="rdv.prospect?.client?.email" 
                      :href="`mailto:${rdv.prospect.client.email}`" 
                      class="contact-link email-link"
                      @click.stop
                    >
                      <i class="fas fa-envelope"></i>
                      {{ rdv.prospect.client.email }}
                    </a>
                  </div>
                </td>
                <td class="commercial-cell">
                  <div class="commercial-info">
                    <div class="commercial-name" v-if="rdv.commercialCreateur">
                      <i class="fas fa-user-tie"></i>
                      {{ rdv.commercialCreateur.nom }} {{ rdv.commercialCreateur.prenom }}
                    </div>
                    <div class="commercial-name" v-else-if="rdv.prospect?.commercial">
                      <i class="fas fa-user-tie"></i>
                      {{ rdv.prospect.commercial.nom }} {{ rdv.prospect.commercial.prenom }}
                    </div>
                    <div class="commercial-unknown" v-else>
                      <i class="fas fa-question-circle"></i>
                      Non renseigné
                    </div>
                  </div>
                </td>
                <td class="technicien-cell">
                  <div class="technicien-info">
                    <div class="technicien-name">{{ rdv.technicien?.nom }} {{ rdv.technicien?.prenom }}</div>
                    <a 
                      v-if="rdv.technicien?.telephone" 
                      :href="`tel:${rdv.technicien.telephone}`" 
                      class="technicien-phone"
                      @click.stop
                    >
                      <i class="fas fa-phone"></i>
                      {{ rdv.technicien.telephone }}
                    </a>
                  </div>
                </td>
                <td class="intervention-cell">
                  <div class="intervention-info">
                    <div class="intervention-type">{{ rdv.prospect?.typeIntervention }}</div>
                    <div class="intervention-device" v-if="rdv.prospect?.typeAppareil">
                      {{ rdv.prospect.typeAppareil }}
                      <span v-if="rdv.prospect?.nombreAppareils"> ({{ rdv.prospect.nombreAppareils }})</span>
                    </div>
                  </div>
                </td>
                <td class="priority-cell">
                  <span class="priority-badge" :class="getPriorityClass(rdv.prospect?.priorite)">
                    {{ getPriorityIcon(rdv.prospect?.priorite) }} {{ rdv.prospect?.priorite }}
                  </span>
                </td>
                <td class="status-cell">
                  <span class="status-badge" :class="getStatusClass(rdv.statut)">
                    {{ getStatusIcon(rdv.statut) }} {{ rdv.statut }}
                  </span>
                </td>
                <td class="actions-cell">
                  <div class="actions-buttons">
                    <button 
                      @click.stop="openRdvModal(rdv)" 
                      class="action-btn view-btn"
                      title="Voir les détails"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal de détails RDV -->
  <div v-if="showRdvModal" class="modal-overlay" @click="closeRdvModal">
    <div class="modal-content" @click.stop>
      <!-- Header -->
      <div class="modal-header">
        <div class="header-content">
          <h3>📅 Détails du Rendez-vous</h3>
          <p class="rdv-id" v-if="selectedRdv">ID: {{ selectedRdv._id || 'N/A' }}</p>
        </div>
        <button @click="closeRdvModal" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- État de chargement -->
      <div v-if="!selectedRdv" class="modal-loading">
        <div class="loading-spinner">
          <i class="fas fa-spinner fa-spin"></i>
        </div>
        <p>Chargement des détails...</p>
      </div>

      <!-- Body -->
      <div v-else class="modal-body">
        <!-- Informations générales -->
        <div class="section">
          <div class="section-header">
            <div class="section-icon">
              <i class="fas fa-calendar-check"></i>
            </div>
            <h4>📅 Informations générales</h4>
          </div>
          <div class="section-content">
            <div class="form-row">
              <div class="form-group">
                <label>Date</label>
                <div class="form-control readonly">
                  {{ new Date(selectedRdv.dateRdv).toLocaleDateString('fr-FR', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  }) }}
                </div>
              </div>
              <div class="form-group">
                <label>Horaires</label>
                <div class="form-control readonly">
                  {{ selectedRdv.heureDebut }} - {{ selectedRdv.heureFin }}
                </div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>Durée estimée</label>
                <div class="form-control readonly">
                  {{ selectedRdv.dureeEstimee }}h
                </div>
              </div>
              <div class="form-group">
                <label>Statut</label>
                <div class="form-control readonly">
                  <span class="status-badge" :class="getStatusClass(selectedRdv.statut)">
                    {{ getStatusIcon(selectedRdv.statut) }} {{ selectedRdv.statut }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Informations Client -->
        <div class="section" v-if="selectedRdv.prospect?.client">
          <div class="section-header">
            <div class="section-icon">
              <i class="fas fa-user"></i>
            </div>
            <h4>👤 Informations Client</h4>
          </div>
          <div class="section-content">
            <div class="form-row">
              <div class="form-group">
                <label>Nom</label>
                <div class="form-control readonly">
                  {{ selectedRdv.prospect.client.nomComplet }}
                </div>
              </div>
              <div class="form-group">
                <label>Téléphone</label>
                <div class="form-control readonly">
                  <a v-if="selectedRdv.prospect.client.telephone" :href="`tel:${selectedRdv.prospect.client.telephone}`" class="contact-link">
                    <i class="fas fa-phone"></i>
                    {{ selectedRdv.prospect.client.telephone }}
                  </a>
                  <span v-else>Non renseigné</span>
                </div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>Email</label>
                <div class="form-control readonly">
                  <a v-if="selectedRdv.prospect.client.email" :href="`mailto:${selectedRdv.prospect.client.email}`" class="contact-link">
                    <i class="fas fa-envelope"></i>
                    {{ selectedRdv.prospect.client.email }}
                  </a>
                  <span v-else>Non renseigné</span>
                </div>
              </div>
              <div class="form-group" v-if="selectedRdv.prospect.client.adresse">
                <label>Adresse</label>
                <div class="form-control readonly">
                  {{ selectedRdv.prospect.client.adresse.rue }}<br>
                  {{ selectedRdv.prospect.client.adresse.codePostal }} {{ selectedRdv.prospect.client.adresse.ville }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Informations Technicien -->
        <div class="section" v-if="selectedRdv.technicien">
          <div class="section-header">
            <div class="section-icon">
              <i class="fas fa-wrench"></i>
            </div>
            <h4>🔧 Technicien Assigné</h4>
          </div>
          <div class="section-content">
            <div class="form-row">
              <div class="form-group">
                <label>Nom</label>
                <div class="form-control readonly">
                  {{ selectedRdv.technicien.nom }} {{ selectedRdv.technicien.prenom }}
                </div>
              </div>
              <div class="form-group">
                <label>Téléphone</label>
                <div class="form-control readonly">
                  <a v-if="selectedRdv.technicien.telephone" :href="`tel:${selectedRdv.technicien.telephone}`" class="contact-link">
                    <i class="fas fa-phone"></i>
                    {{ selectedRdv.technicien.telephone }}
                  </a>
                  <span v-else>Non renseigné</span>
                </div>
              </div>
            </div>
            <div class="form-row" v-if="selectedRdv.technicien.email">
              <div class="form-group">
                <label>Email</label>
                <div class="form-control readonly">
                  <a :href="`mailto:${selectedRdv.technicien.email}`" class="contact-link">
                    <i class="fas fa-envelope"></i>
                    {{ selectedRdv.technicien.email }}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Informations Commercial -->
        <div class="section">
          <div class="section-header">
            <div class="section-icon">
              <i class="fas fa-user-tie"></i>
            </div>
            <h4>💼 Commercial Créateur</h4>
          </div>
          <div class="section-content">
            <div class="form-row" v-if="selectedRdv.commercialCreateur">
              <div class="form-group">
                <label>Nom</label>
                <div class="form-control readonly">
                  {{ selectedRdv.commercialCreateur.nom }} {{ selectedRdv.commercialCreateur.prenom }}
                </div>
              </div>
              <div class="form-group" v-if="selectedRdv.commercialCreateur.email">
                <label>Email</label>
                <div class="form-control readonly">
                  <a :href="`mailto:${selectedRdv.commercialCreateur.email}`" class="contact-link">
                    <i class="fas fa-envelope"></i>
                    {{ selectedRdv.commercialCreateur.email }}
                  </a>
                </div>
              </div>
            </div>
            <div class="form-row" v-else-if="selectedRdv.prospect?.commercial">
              <div class="form-group">
                <label>Nom</label>
                <div class="form-control readonly">
                  {{ selectedRdv.prospect.commercial.nom }} {{ selectedRdv.prospect.commercial.prenom }}
                </div>
              </div>
              <div class="form-group" v-if="selectedRdv.prospect.commercial.email">
                <label>Email</label>
                <div class="form-control readonly">
                  <a :href="`mailto:${selectedRdv.prospect.commercial.email}`" class="contact-link">
                    <i class="fas fa-envelope"></i>
                    {{ selectedRdv.prospect.commercial.email }}
                  </a>
                </div>
              </div>
            </div>
            <div class="form-row" v-else>
              <div class="form-group">
                <label>Statut</label>
                <div class="form-control readonly">
                  <span class="text-muted">
                    <i class="fas fa-question-circle"></i>
                    Commercial non renseigné
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Détails Intervention -->
        <div class="section" v-if="selectedRdv.prospect">
          <div class="section-header">
            <div class="section-icon">
              <i class="fas fa-tools"></i>
            </div>
            <h4>🛠️ Détails de l'Operation</h4>
          </div>
          <div class="section-content">
            <div class="form-row">
              <div class="form-group">
                <label>Type d'intervention</label>
                <div class="form-control readonly">
                  {{ selectedRdv.prospect.typeIntervention || 'Non spécifié' }}
                </div>
              </div>
              <div class="form-group">
                <label>Priorité</label>
                <div class="form-control readonly">
                  <span v-if="selectedRdv.prospect.priorite" class="priority-badge" :class="getPriorityClass(selectedRdv.prospect.priorite)">
                    {{ getPriorityIcon(selectedRdv.prospect.priorite) }} {{ selectedRdv.prospect.priorite }}
                  </span>
                  <span v-else>Non définie</span>
                </div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>Type d'appareil</label>
                <div class="form-control readonly">
                  {{ selectedRdv.prospect.typeAppareil || 'Non spécifié' }}
                </div>
              </div>
              <div class="form-group">
                <label>Nombre d'appareils</label>
                <div class="form-control readonly">
                  {{ selectedRdv.prospect.nombreAppareils || 0 }} appareil(s)
                </div>
              </div>
            </div>
            <div class="form-group" v-if="selectedRdv.prospect.description">
              <label>Description</label>
              <div class="form-control readonly description-field">
                {{ selectedRdv.prospect.description }}
              </div>
            </div>
          </div>
        </div>

        <!-- Notes et Commentaires -->
        <div class="section" v-if="selectedRdv.notes || selectedRdv.prospect?.commentaires">
          <div class="section-header">
            <div class="section-icon">
              <i class="fas fa-sticky-note"></i>
            </div>
            <h4>📝 Notes et Commentaires</h4>
          </div>
          <div class="section-content">
            <div class="form-group" v-if="selectedRdv.notes">
              <label>Notes du RDV</label>
              <div class="form-control readonly description-field">
                {{ selectedRdv.notes }}
              </div>
            </div>
            <div class="form-group" v-if="selectedRdv.prospect?.commentaires">
              <label>Commentaires du prospect</label>
              <div class="form-control readonly description-field">
                {{ selectedRdv.prospect.commentaires }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <div class="footer-info">
          <i class="fas fa-eye"></i>
          Mode lecture seule
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useToast } from 'vue-toastification'
import { API_BASE_URL } from '@/config/api'

export default {
  name: 'PlanningManager',
  props: {
    userRole: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const toast = useToast()
    const currentView = ref('Semaine')
    const currentDate = ref(new Date())
    const rdvs = ref([])
    const loading = ref(false)
    const showRdvModal = ref(false)
    const selectedRdv = ref(null)
    const isMobile = ref(window.innerWidth <= 768)
    const selectedMonthDay = ref(null)

    // Détecter le mode mobile et forcer la vue Jour ou Tableau
    const handleResize = () => {
      isMobile.value = window.innerWidth <= 768
      // Sur mobile, on garde toutes les vues disponibles mais on adapte l'affichage
      // Ne plus forcer la vue Jour automatiquement
    }
    
    const views = ['Jour', 'Semaine', 'Mois', 'Tableau']
    const hours = Array.from({length: 24}, (_, i) => i)
    const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
    const monthDays = Array.from({length: 30}, (_, i) => i + 1)

    const currentPeriodLabel = computed(() => {
      if (!currentDate.value || !(currentDate.value instanceof Date)) {
        return 'Date invalide'
      }
      return currentDate.value.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
    })

    // Charger les RDV depuis l'API
    const loadRdvs = async () => {
      loading.value = true
      try {
        const token = localStorage.getItem('token')
        
        // Récupérer plus de RDV pour le calendrier (100 par défaut)
        const response = await fetch(`/api/rdv?limit=100`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const result = await response.json()
          // Filtrer les interventions annulées uniquement pour les techniciens
          if (props.userRole === 'Technicien') {
            rdvs.value = (result.rdvs || []).filter(rdv => rdv.statut !== 'Annulé')
          } else {
            rdvs.value = result.rdvs || []
          }
          console.log('RDV chargés (hors annulés pour techniciens):', rdvs.value.length)
          console.log('Exemple RDV:', rdvs.value[0]) // Pour debug
          
          // Notification supprimée pour éviter le spam
          if (rdvs.value.length === 0) {
            toast.info('Aucun rendez-vous trouvé')
          }
        } else {
          console.error('Erreur lors du chargement des RDV:', response.status)
          const errorData = await response.json()
          console.error('Détails erreur:', errorData)
          toast.error('Erreur lors du chargement des rendez-vous')
        }
      } catch (error) {
        console.error('Erreur lors du chargement des RDV:', error)
        toast.error('Erreur de connexion au serveur')
      } finally {
        loading.value = false
      }
    }

    const previousPeriod = () => {
      if (!currentDate.value || !(currentDate.value instanceof Date)) {
        currentDate.value = new Date()
        return
      }
      
      const date = new Date(currentDate.value)
      if (currentView.value === 'Jour') {
        date.setDate(date.getDate() - 1)
      } else if (currentView.value === 'Semaine') {
        date.setDate(date.getDate() - 7)
      } else if (currentView.value === 'Mois') {
        date.setMonth(date.getMonth() - 1)
      }
      currentDate.value = date
      loadRdvs() // Recharger les RDV pour la nouvelle période
    }

    const nextPeriod = () => {
      if (!currentDate.value || !(currentDate.value instanceof Date)) {
        currentDate.value = new Date()
        return
      }
      
      const date = new Date(currentDate.value)
      if (currentView.value === 'Jour') {
        date.setDate(date.getDate() + 1)
      } else if (currentView.value === 'Semaine') {
        date.setDate(date.getDate() + 7)
      } else if (currentView.value === 'Mois') {
        date.setMonth(date.getMonth() + 1)
      }
      currentDate.value = date
      loadRdvs() // Recharger les RDV pour la nouvelle période
    }

    const createRdv = () => {
      console.log('Créer RDV')
      toast.info('Fonctionnalité de création de RDV à implémenter')
    }

    // Ouvrir le modal de détails RDV
    const openRdvModal = (rdv) => {
      // Afficher immédiatement le modal
      showRdvModal.value = true
      selectedRdv.value = null
      
      // Simuler un chargement rapide pour une meilleure UX
      setTimeout(() => {
        selectedRdv.value = rdv
      }, 100)
    }

    // Fermer le modal de détails RDV
    const closeRdvModal = () => {
      showRdvModal.value = false
      selectedRdv.value = null
    }

    // Obtenir les RDV pour un jour et une heure spécifiques
    const getRdvs = (dayIndex, hour) => {
      if (!rdvs.value || rdvs.value.length === 0 || !currentDate.value || !(currentDate.value instanceof Date)) return []
      
      return rdvs.value.filter(rdv => {
        const rdvDate = new Date(rdv.dateRdv)
        const rdvHour = parseInt(rdv.heureDebut.split(':')[0])
        
        // Calculer la date du jour dans la semaine courante
        const startOfWeek = new Date(currentDate.value)
        startOfWeek.setDate(currentDate.value.getDate() - currentDate.value.getDay() + 1) // Lundi
        const targetDate = new Date(startOfWeek)
        targetDate.setDate(startOfWeek.getDate() + dayIndex - 1)
        
        return rdvDate.toDateString() === targetDate.toDateString() && rdvHour === hour
      })
    }

    // Obtenir les RDV pour un jour spécifique (vue mois)
    const getDayRdvs = (day) => {
      if (!rdvs.value || rdvs.value.length === 0 || !currentDate.value || !(currentDate.value instanceof Date)) return []
      
      const targetDate = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth(), day)
      
      return rdvs.value.filter(rdv => {
        const rdvDate = new Date(rdv.dateRdv)
        return rdvDate.toDateString() === targetDate.toDateString()
      })
    }

    // Fonctions pour la vue Mois améliorée
    const getDaysInMonth = () => {
      if (!currentDate.value || !(currentDate.value instanceof Date)) return 30
      const year = currentDate.value.getFullYear()
      const month = currentDate.value.getMonth()
      return new Date(year, month + 1, 0).getDate()
    }

    const getFirstDayOffset = () => {
      if (!currentDate.value || !(currentDate.value instanceof Date)) return 0
      const year = currentDate.value.getFullYear()
      const month = currentDate.value.getMonth()
      const firstDay = new Date(year, month, 1).getDay()
      // Convertir dimanche (0) en 6, lundi (1) en 0, etc.
      return firstDay === 0 ? 6 : firstDay - 1
    }

    const isToday = (day) => {
      if (!currentDate.value || !(currentDate.value instanceof Date)) return false
      const today = new Date()
      return today.getDate() === day && 
             today.getMonth() === currentDate.value.getMonth() && 
             today.getFullYear() === currentDate.value.getFullYear()
    }

    const selectMonthDay = (day) => {
      selectedMonthDay.value = selectedMonthDay.value === day ? null : day
    }

    // Calculer la date pour chaque jour de la semaine
    const getWeekDayDate = (dayIndex) => {
      if (!currentDate.value || !(currentDate.value instanceof Date)) {
        return '?'
      }
      const startOfWeek = new Date(currentDate.value)
      startOfWeek.setDate(currentDate.value.getDate() - currentDate.value.getDay() + 1) // Lundi
      const targetDate = new Date(startOfWeek)
      targetDate.setDate(startOfWeek.getDate() + dayIndex)
      return targetDate.getDate()
    }

    // Fonctions helper pour les classes CSS
    const getStatusClass = (statut) => {
      const classes = {
        'Planifié': 'status-planned',
        'En cours': 'status-progress',
        'Terminé': 'status-completed',
        'Reporté': 'status-postponed',
        'Annulé': 'status-cancelled'
      }
      return classes[statut] || 'status-default'
    }

    const getPriorityClass = (priorite) => {
      const classes = {
        'Haute': 'priority-high',
        'Normale': 'priority-normal',
        'Basse': 'priority-low'
      }
      return classes[priorite] || 'priority-normal'
    }

    // Fonction pour obtenir la classe de région basée sur la ville
    const getRegionClass = (ville) => {
      if (!ville) {
        return 'region-default'
      }
      
      // Nord-Est: Blanc
      const nordEst = ['Tunis', 'Ariana', 'Ben Arous', 'Manouba', 'Zaghouan', 'Bizerte']
      if (nordEst.includes(ville)) return 'region-nord-est'
      
      // Nord-Ouest: Gris
      const nordOuest = ['Jendouba', 'Béja', 'Le Kef', 'Siliana']
      if (nordOuest.includes(ville)) return 'region-nord-ouest'
      
      // Centre-Est: Bleu
      const centreEst = ['Nabeul', 'Sousse', 'Monastir', 'Mahdia']
      if (centreEst.includes(ville)) return 'region-centre-est'
      
      // Sud: Rouge (fusion Sud-Est + Sud-Ouest + Centre-Ouest)
      const sud = ['Sfax', 'Gabès', 'Médenine', 'Tataouine', 'Kairouan', 'Kasserine', 'Sidi Bouzid', 'Gafsa', 'Tozeur', 'Kébili']
      if (sud.includes(ville)) return 'region-sud'
      
      return 'region-default'
    }

    const getChecklistIcon = (statut) => {
      const icons = {
        'Non fait': 'fa-circle',
        'En cours': 'fa-clock',
        'Terminé': 'fa-check-circle',
        'N/A': 'fa-minus-circle'
      }
      return icons[statut] || 'fa-circle'
    }

    const getChecklistStatusClass = (statut) => {
      const classes = {
        'Non fait': 'checklist-todo',
        'En cours': 'checklist-progress',
        'Terminé': 'checklist-done',
        'N/A': 'checklist-na'
      }
      return classes[statut] || 'checklist-todo'
    }

    // RDV triés par date pour la vue tableau
    const sortedRdvs = computed(() => {
      return [...rdvs.value].sort((a, b) => {
        const dateA = new Date(a.dateRdv)
        const dateB = new Date(b.dateRdv)
        if (dateA.getTime() === dateB.getTime()) {
          // Si même date, trier par heure
          return a.heureDebut.localeCompare(b.heureDebut)
        }
        return dateA - dateB
      })
    })

    // Formatage des dates pour le tableau
    const formatTableDate = (dateString) => {
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }

    const formatDayName = (dateString) => {
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', { weekday: 'short' })
    }

    // Icônes pour les priorités et statuts
    const getPriorityIcon = (priorite) => {
      const icons = {
        'Haute': '🔴',
        'Normale': '🟡',
        'Basse': '🟢'
      }
      return icons[priorite] || '⚪'
    }

    const getStatusIcon = (statut) => {
      const icons = {
        'Planifié': '📅',
        'En cours': '⏳',
        'Terminé': '✅',
        'Reporté': '📆',
        'Annulé': '❌'
      }
      return icons[statut] || '📍'
    }

    // Fonctions pour la vue jour
    const formatDayTitle = (date) => {
      console.log('🔍 formatDayTitle - date:', date, 'type:', typeof date, 'isDate:', date instanceof Date)
      
      // Si ce n'est pas une Date, essayer de la convertir
      if (!date || !(date instanceof Date)) {
        if (typeof date === 'string' || typeof date === 'number') {
          const convertedDate = new Date(date)
          if (!isNaN(convertedDate.getTime())) {
            console.log('🔧 Date convertie avec succès:', convertedDate)
            return convertedDate.toLocaleDateString('fr-FR', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })
          }
        }
        console.log('❌ Date invalide, utilisation de la date actuelle')
        const today = new Date()
        return today.toLocaleDateString('fr-FR', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        })
      }
      
      return date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    }

    const formatDaySubtitle = (date) => {
      if (!date || !(date instanceof Date)) {
        return ""
      }
      
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(today.getDate() + 1)
      const yesterday = new Date(today)
      yesterday.setDate(today.getDate() - 1)

      if (date.toDateString() === today.toDateString()) {
        return "Aujourd'hui"
      } else if (date.toDateString() === tomorrow.toDateString()) {
        return "Demain"
      } else if (date.toDateString() === yesterday.toDateString()) {
        return "Hier"
      }
      return ""
    }

    const getDayRdvCount = (date) => {
      console.log('🔍 getDayRdvCount - date:', date, 'isDate:', date instanceof Date, 'rdvs count:', rdvs.value?.length)
      
      // Convertir en Date si nécessaire
      let workingDate = date
      if (!date || !(date instanceof Date)) {
        if (typeof date === 'string' || typeof date === 'number') {
          workingDate = new Date(date)
          if (isNaN(workingDate.getTime())) {
            console.log('❌ Date invalide dans getDayRdvCount')
            return 0
          }
        } else {
          console.log('❌ Date invalide dans getDayRdvCount')
          return 0
        }
      }
      
      if (!rdvs.value || rdvs.value.length === 0) return 0
      
      const filteredRdvs = rdvs.value.filter(rdv => {
        const rdvDate = new Date(rdv.dateRdv)
        const match = rdvDate.toDateString() === workingDate.toDateString()
        if (match) {
          console.log('🔍 RDV trouvé pour le jour:', rdv.prospect?.client?.nomComplet, rdv.heureDebut)
        }
        return match
      })
      console.log('🔍 Total RDV pour le jour:', filteredRdvs.length)
      return filteredRdvs.length
    }

    const getDayHourRdvs = (date, hour) => {
      console.log('🔍 getDayHourRdvs - date:', date, 'hour:', hour, 'isDate:', date instanceof Date)
      
      // Convertir en Date si nécessaire
      let workingDate = date
      if (!date || !(date instanceof Date)) {
        if (typeof date === 'string' || typeof date === 'number') {
          workingDate = new Date(date)
          if (isNaN(workingDate.getTime())) {
            console.log('❌ Date invalide dans getDayHourRdvs')
            return []
          }
        } else {
          console.log('❌ Date invalide dans getDayHourRdvs')
          return []
        }
      }
      
      if (!rdvs.value || rdvs.value.length === 0) return []
      
      const filteredRdvs = rdvs.value.filter(rdv => {
        const rdvDate = new Date(rdv.dateRdv)
        const rdvHour = parseInt(rdv.heureDebut.split(':')[0])
        const dateMatch = rdvDate.toDateString() === workingDate.toDateString()
        const hourMatch = rdvHour === hour
        
        if (dateMatch && hourMatch) {
          console.log('🔍 RDV trouvé pour', hour + 'h:', rdv.prospect?.client?.nomComplet)
        }
        
        return dateMatch && hourMatch
      })
      return filteredRdvs
    }

    // Fonctions pour le sélecteur de date
    const formatDateForInput = (date) => {
      if (!date || !(date instanceof Date)) {
        return new Date().toISOString().split('T')[0]
      }
      return date.toISOString().split('T')[0]
    }

    const onDateChange = (event) => {
      const selectedDate = new Date(event.target.value)
      if (selectedDate && !isNaN(selectedDate.getTime())) {
        currentDate.value = selectedDate
        loadRdvs() // Recharger les RDV pour la nouvelle date
        toast.info(`📅 Navigation vers ${formatDayTitle(selectedDate)}`)
      }
    }

    // Fonction pour aller à aujourd'hui
    const goToToday = () => {
      currentDate.value = new Date()
      loadRdvs()
      toast.success('📅 Retour à aujourd\'hui')
    }

    // Charger les RDV au montage du composant
    onMounted(() => {
      loadRdvs()
      // Initialiser le mode responsive
      handleResize()
      window.addEventListener('resize', handleResize)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize)
    })

    return {
      currentView,
      currentDate,
      views,
      hours,
      weekDays,
      monthDays,
      rdvs,
      loading,
      showRdvModal,
      selectedRdv,
      selectedMonthDay,
      currentPeriodLabel,
      previousPeriod,
      nextPeriod,
      createRdv,
      openRdvModal,
      closeRdvModal,
      getRdvs,
      getDayRdvs,
      getDaysInMonth,
      getFirstDayOffset,
      isToday,
      selectMonthDay,
      getWeekDayDate,
      getStatusClass,
      getPriorityClass,
      getRegionClass,
      getChecklistIcon,
      getChecklistStatusClass,
      sortedRdvs,
      formatTableDate,
      formatDayName,
      getPriorityIcon,
      getStatusIcon,
      formatDayTitle,
      formatDaySubtitle,
      getDayRdvCount,
      getDayHourRdvs,
      formatDateForInput,
      onDateChange,
      goToToday,
      loadRdvs,
      isMobile
    }
  }
}
</script>

<style scoped>
/* Variables CSS */
:root {
  --primary-blue: #001BB7;
  --secondary-blue: #0046FF;
  --success-green: #10B981;
  --warning-orange: #F59E0B;
  --danger-red: #EF4444;
}

.planning-manager { 
  --primary-blue: #001BB7;
  --secondary-blue: #0046FF;
  --success-green: #10B981;
  --warning-orange: #F59E0B;
  --danger-red: #EF4444;
  padding: 1.5rem; 
  background: linear-gradient(135deg, 
    rgba(248, 250, 252, 0.95) 0%, 
    rgba(241, 245, 249, 0.8) 50%, 
    rgba(226, 232, 240, 0.6) 100%);
  min-height: 70vh;
  position: relative;
  overflow: hidden;
}

.planning-manager::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle at 30% 20%, rgba(0, 27, 183, 0.05) 0%, transparent 50%),
              radial-gradient(circle at 70% 80%, rgba(0, 70, 255, 0.03) 0%, transparent 50%);
  animation: backgroundFloat 20s ease-in-out infinite;
  pointer-events: none;
  z-index: 0;
}

@keyframes backgroundFloat {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  33% { transform: translate(-20px, -20px) rotate(1deg); }
  66% { transform: translate(20px, -10px) rotate(-1deg); }
}

.header { 
  display: flex; 
  justify-content: space-between; 
  align-items: center;
  margin-bottom: 2rem; 
  padding: 1.5rem 2rem;
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.95) 0%, 
    rgba(248, 250, 252, 0.9) 100%);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 
    0 8px 32px rgba(0, 27, 183, 0.12),
    0 2px 8px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  position: relative;
  z-index: 1;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  flex-wrap: wrap;
  gap: 1rem;
}

.header:hover {
  transform: translateY(-2px);
  box-shadow: 
    0 12px 40px rgba(0, 27, 183, 0.15),
    0 4px 12px rgba(0, 0, 0, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.view-tabs { 
  display: flex; 
  gap: 0.75rem;
  padding: 0.5rem;
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.4) 0%, 
    rgba(248, 250, 252, 0.3) 100%);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  min-width: 0;
  flex-shrink: 0;
}

.view-tabs button { 
  padding: 0.875rem 1.75rem; 
  border: 1px solid rgba(0, 27, 183, 0.15); 
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.8) 0%, 
    rgba(248, 250, 252, 0.6) 100%);
  backdrop-filter: blur(8px);
  border-radius: 14px;
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--primary-blue);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.view-tabs button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  transition: left 0.6s ease;
}

.view-tabs button:hover::before {
  left: 100%;
}

.view-tabs button:hover {
  transform: translateY(-2px) scale(1.02);
  border-color: rgba(0, 27, 183, 0.3);
  box-shadow: 
    0 8px 25px rgba(0, 27, 183, 0.15),
    0 2px 8px rgba(0, 0, 0, 0.05);
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.95) 0%, 
    rgba(240, 249, 255, 0.8) 100%);
}

.view-tabs button.active {
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
  border-color: transparent;
  box-shadow: 
    0 6px 20px rgba(0, 27, 183, 0.3),
    0 2px 8px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
}

.view-tabs button.active:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 
    0 10px 30px rgba(0, 27, 183, 0.4),
    0 4px 12px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.header-info {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  min-width: 0;
  flex: 1;
  justify-content: flex-end;
}

.rdv-count {
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.9) 0%, 
    rgba(240, 249, 255, 0.8) 100%);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 27, 183, 0.2);
  border-radius: 16px;
  font-weight: 700;
  font-size: 1rem;
  color: var(--primary-blue);
  box-shadow: 
    0 4px 15px rgba(0, 27, 183, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  transition: all 0.3s ease;
}

.date-nav { 
  display: flex; 
  align-items: center; 
  gap: 1rem; 
  flex-wrap: wrap;
  min-width: 0;
}

.date-nav button {
  padding: 0.5rem 1rem;
  border: 2px solid rgba(0, 27, 183, 0.2);
  background: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  color: var(--primary-blue);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.date-nav button:hover:not(:disabled) {
  background: var(--primary-blue);
  color: white;
  transform: translateY(-1px);
}

.date-nav button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.date-nav span {
  font-weight: 700;
  color: var(--primary-blue);
  font-size: 1.1rem;
  min-width: 200px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.day-date-picker {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  min-width: 250px;
}

.date-input {
  padding: 0.5rem 0.75rem;
  border: 2px solid rgba(0, 27, 183, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.9);
  color: var(--primary-blue);
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.date-input:hover, .date-input:focus {
  border-color: var(--primary-blue);
  background: white;
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 27, 183, 0.1);
}

.current-day-label {
  font-size: 0.85rem;
  color: var(--primary-blue);
  font-weight: 600;
}

.refresh-btn {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 
    0 4px 15px rgba(0, 27, 183, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.refresh-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 
    0 8px 25px rgba(0, 27, 183, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: 
    0 3px 10px rgba(0, 27, 183, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.calendar-grid {
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.95) 0%, 
    rgba(248, 250, 252, 0.9) 100%);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 
    0 10px 40px rgba(0, 27, 183, 0.12),
    0 4px 16px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  position: relative;
  z-index: 1;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.calendar-grid:hover {
  transform: translateY(-1px);
  box-shadow: 
    0 15px 50px rgba(0, 27, 183, 0.15),
    0 6px 20px rgba(0, 0, 0, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

/* Structure du calendrier */
.week-view {
  display: flex;
  flex-direction: column;
  height: 600px;
}

/* Header du calendrier semaine */
.calendar-header {
  display: grid;
  grid-template-columns: 100px repeat(7, 1fr);
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border-radius: 16px 16px 0 0;
  overflow: hidden;
}

.time-column-header {
  padding: 1rem 0.75rem;
  font-weight: 700;
  font-size: 0.9rem;
  text-align: center;
  background: rgba(255, 255, 255, 0.1);
  border-right: 1px solid rgba(255, 255, 255, 0.2);
}

.day-header {
  padding: 1rem 0.75rem;
  text-align: center;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.day-header:last-child {
  border-right: none;
}

.day-header:hover {
  background: rgba(255, 255, 255, 0.1);
}

.day-name {
  font-weight: 700;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.day-date {
  font-size: 1.2rem;
  font-weight: 800;
  opacity: 0.9;
}

/* Corps du calendrier semaine */
.calendar-body {
  flex: 1;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 0 0 16px 16px;
}

.time-row {
  display: grid;
  grid-template-columns: 100px repeat(7, 1fr);
  min-height: 60px;
  border-bottom: 1px solid rgba(0, 27, 183, 0.1);
}

.time-row:last-child {
  border-bottom: none;
}

.time-column {
  padding: 1rem 0.75rem;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.05), rgba(0, 70, 255, 0.02));
  border-right: 2px solid rgba(0, 27, 183, 0.1);
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.time-label {
  font-weight: 700;
  color: var(--primary-blue);
  font-size: 0.85rem;
}

.day-column {
  border-right: 1px solid rgba(0, 27, 183, 0.1);
}

.day-column:last-child {
  border-right: none;
}

.time-cell {
  padding: 0.5rem;
  min-height: 60px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.time-cell:hover {
  background: rgba(0, 27, 183, 0.02);
}

/* RDV dans la vue semaine */
.rdv-item {
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.95) 0%, 
    rgba(240, 249, 255, 0.9) 100%);
  border: 1px solid rgba(0, 27, 183, 0.2);
  border-left: 4px solid var(--primary-blue);
  border-radius: 8px;
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 27, 183, 0.1);
  font-size: 0.75rem;
}

.rdv-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(0, 27, 183, 0.2);
  border-left-color: var(--secondary-blue);
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.98) 0%, 
    rgba(240, 249, 255, 0.95) 100%);
}

/* ========================================
   Couleurs par Région - RDV Calendrier
   ======================================== */

/* Nord-Est: Blanc (Tunis, Ariana, Ben Arous, Manouba, Nabeul, Zaghouan, Bizerte) */
.rdv-item.region-nord-est,
.day-rdv-item.region-nord-est {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 250, 250, 0.9) 100%) !important;
  border-left: 4px solid #e5e7eb !important;
  border-color: rgba(229, 231, 235, 0.6) !important;
}
.rdv-item.region-nord-est:hover,
.day-rdv-item.region-nord-est:hover {
  background: linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(245, 245, 245, 0.95) 100%) !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15) !important;
}

/* Nord-Ouest: Gris (Jendouba, Béja, Le Kef, Siliana) */
.rdv-item.region-nord-ouest,
.day-rdv-item.region-nord-ouest {
  background: linear-gradient(135deg, rgba(107, 114, 128, 0.25) 0%, rgba(75, 85, 99, 0.15) 100%) !important;
  border-left: 4px solid #6b7280 !important;
  border-color: rgba(107, 114, 128, 0.4) !important;
}
.rdv-item.region-nord-ouest:hover,
.day-rdv-item.region-nord-ouest:hover {
  background: linear-gradient(135deg, rgba(107, 114, 128, 0.35) 0%, rgba(75, 85, 99, 0.25) 100%) !important;
  box-shadow: 0 4px 15px rgba(107, 114, 128, 0.4) !important;
}

/* Centre-Est: Bleu (Sousse, Monastir, Mahdia) */
.rdv-item.region-centre-est,
.day-rdv-item.region-centre-est {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(37, 99, 235, 0.15) 100%) !important;
  border-left: 4px solid #3b82f6 !important;
  border-color: rgba(59, 130, 246, 0.4) !important;
}
.rdv-item.region-centre-est:hover,
.day-rdv-item.region-centre-est:hover {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.35) 0%, rgba(37, 99, 235, 0.25) 100%) !important;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4) !important;
}

/* Sud: Rouge (Sfax, Gabès, Médenine, Tataouine, Kairouan, Kasserine, Sidi Bouzid, Gafsa, Tozeur, Kébili) */
.rdv-item.region-sud,
.day-rdv-item.region-sud {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.25) 0%, rgba(220, 38, 38, 0.15) 100%) !important;
  border-left: 4px solid #ef4444 !important;
  border-color: rgba(239, 68, 68, 0.4) !important;
}
.rdv-item.region-sud:hover,
.day-rdv-item.region-sud:hover {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.35) 0%, rgba(220, 38, 38, 0.25) 100%) !important;
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.4) !important;
}

/* Région par défaut (pas de ville définie) */
.rdv-item.region-default,
.day-rdv-item.region-default {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 249, 255, 0.9) 100%) !important;
  border-left: 4px solid var(--primary-blue) !important;
}

.rdv-client {
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.rdv-tech {
  color: #6b7280;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.25rem;
}

.rdv-ville {
  color: #059669;
  font-size: 0.65rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.15rem;
  font-weight: 500;
}

.rdv-time {
  color: var(--primary-blue);
  font-weight: 600;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

/* Vue mois */
.month-view {
  min-height: 500px;
}

.month-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border-radius: 16px 16px 0 0;
  overflow: hidden;
}

.month-day-header {
  padding: 0.75rem;
  text-align: center;
  font-weight: 700;
  font-size: 0.85rem;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
}

.month-day-header:last-child {
  border-right: none;
}

.month-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background: rgba(0, 27, 183, 0.1);
  border-radius: 0 0 16px 16px;
  overflow: hidden;
}

.day-cell {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 80px;
}

.day-cell.empty-cell {
  background: rgba(248, 250, 252, 0.5);
  cursor: default;
}

.day-cell.empty-cell:hover {
  transform: none;
}

.day-cell.today {
  background: rgba(0, 27, 183, 0.1);
  border: 2px solid var(--primary-blue);
}

.day-cell.today .day-number {
  background: var(--primary-blue);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 50%;
  display: inline-block;
}

.day-cell.has-rdv {
  background: rgba(240, 249, 255, 0.95);
}

.day-cell:hover:not(.empty-cell) {
  background: rgba(240, 249, 255, 0.95);
  transform: scale(1.02);
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.15);
}

.day-number {
  font-weight: 700;
  color: var(--primary-blue);
  font-size: 0.9rem;
}

.day-rdvs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  align-items: center;
}

.rdv-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--primary-blue);
  cursor: pointer;
  transition: all 0.3s ease;
}

.rdv-dot.status-planned {
  background: #3b82f6;
}

.rdv-dot.status-progress {
  background: #f59e0b;
}

.rdv-dot.status-completed {
  background: #10b981;
}

.rdv-dot.status-postponed {
  background: #8b5cf6;
}

.rdv-dot.status-cancelled {
  background: #ef4444;
}

.rdv-dot:hover {
  transform: scale(1.5);
}

.more-rdvs {
  font-size: 0.7rem;
  color: var(--primary-blue);
  font-weight: 600;
  background: rgba(0, 27, 183, 0.1);
  padding: 0.1rem 0.3rem;
  border-radius: 4px;
}

/* Liste des RDV du jour sélectionné (vue mois) */
.month-day-details {
  margin-top: 1.5rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%);
  border-radius: 16px;
  border: 1px solid rgba(0, 27, 183, 0.1);
  box-shadow: 0 4px 16px rgba(0, 27, 183, 0.1);
}

.month-day-details h4 {
  margin: 0 0 1rem 0;
  color: var(--primary-blue);
  font-size: 1.1rem;
}

.month-rdv-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.month-rdv-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: white;
  border-radius: 10px;
  border: 1px solid rgba(0, 27, 183, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
}

.month-rdv-item:hover {
  transform: translateX(4px);
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.15);
  border-color: var(--primary-blue);
}

.month-rdv-item .rdv-time {
  font-weight: 700;
  color: var(--primary-blue);
  font-size: 0.9rem;
  min-width: 100px;
}

.month-rdv-item .rdv-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.month-rdv-item .rdv-client {
  font-weight: 600;
  color: #1f2937;
  font-size: 0.9rem;
}

.month-rdv-item .rdv-tech {
  font-size: 0.8rem;
  color: #6b7280;
}

.month-rdv-item .status-badge.mini {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
}

/* Classes de priorité et statut */
.priority-high {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

.priority-normal {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
}

.priority-low {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.status-planned {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
}

.status-progress {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
}

.status-completed {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.status-postponed {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  color: white;
}

.status-cancelled {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

.status-default {
  background: linear-gradient(135deg, #6b7280, #4b5563);
  color: white;
}

.rdv-count:hover {
  transform: translateY(-1px) scale(1.05);
  box-shadow: 
    0 6px 20px rgba(0, 27, 183, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.material-quantity {
  background: var(--primary-blue);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.8rem;
}

.material-name {
  flex: 1;
  font-weight: 500;
}


.checklist {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.checklist-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(249, 250, 251, 0.8);
  border-radius: 12px;
  border: 1px solid rgba(0, 27, 183, 0.1);
}

.checklist-item i {
  width: 20px;
  text-align: center;
}

.checklist-item span:first-of-type {
  flex: 1;
  font-weight: 500;
}

.checklist-status {
  padding: 0.25rem 0.5rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
}

.checklist-todo { background: rgba(156, 163, 175, 0.1); color: #6b7280; }
.checklist-progress { background: rgba(245, 158, 11, 0.1); color: #d97706; }
.checklist-done { background: rgba(34, 197, 94, 0.1); color: #059669; }
.checklist-na { background: rgba(156, 163, 175, 0.1); color: #9ca3af; }

.modal-footer {
  padding: 1.5rem;
  border-top: 1px solid rgba(0, 27, 183, 0.1);
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  background: rgba(248, 250, 252, 0.5);
  border-radius: 0 0 20px 20px;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.btn-secondary {
  background: rgba(156, 163, 175, 0.1);
  color: #6b7280;
  border: 2px solid rgba(156, 163, 175, 0.2);
}

.btn-secondary:hover {
  background: rgba(156, 163, 175, 0.2);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(156, 163, 175, 0.3);
}

/* Styles du modal RDV - Template ProspectDetailModal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.95) 0%, 
    rgba(248, 250, 252, 0.9) 100%);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 
    0 20px 40px rgba(0, 27, 183, 0.15),
    0 8px 32px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.3);
  animation: slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideUp {
  from { 
    opacity: 0; 
    transform: translateY(30px) scale(0.95); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0) scale(1); 
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
  border-radius: 20px 20px 0 0;
  position: relative;
  overflow: hidden;
}

.modal-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    linear-gradient(135deg, rgba(255, 255, 255, 0.1), transparent);
  pointer-events: none;
}

.header-content {
  position: relative;
  z-index: 1;
}

.header-content h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.rdv-id {
  margin: 0.5rem 0 0 0;
  font-size: 0.9rem;
  opacity: 0.8;
  font-weight: 400;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: rotate(90deg);
}

.modal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  gap: 1rem;
  color: var(--primary-blue);
}

.modal-loading .loading-spinner {
  font-size: 2rem;
  animation: pulse 2s infinite;
}

.modal-loading p {
  margin: 0;
  font-weight: 600;
  font-size: 1.1rem;
  opacity: 0.8;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.1); }
}

.modal-body {
  padding: 0;
  max-height: calc(90vh - 140px);
  overflow-y: auto;
}

.section {
  border-bottom: 1px solid rgba(0, 27, 183, 0.1);
}

.section:last-child {
  border-bottom: none;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 2rem 1rem 2rem;
  background: linear-gradient(135deg, 
    rgba(0, 27, 183, 0.05) 0%, 
    rgba(0, 70, 255, 0.02) 100%);
  border-bottom: 1px solid rgba(0, 27, 183, 0.1);
}

.section-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.3);
}

.section-header h4 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--primary-blue);
}

.section-content {
  padding: 2rem;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
}

.form-control {
  padding: 0.875rem 1rem;
  border: 2px solid rgba(0, 27, 183, 0.1);
  border-radius: 12px;
  font-size: 0.95rem;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  min-height: 48px;
  display: flex;
  align-items: center;
}

.form-control.readonly {
  background: rgba(248, 250, 252, 0.8);
  border-color: rgba(0, 27, 183, 0.05);
  color: #1f2937;
  cursor: default;
}

.description-field {
  min-height: 60px;
  align-items: flex-start;
  white-space: pre-wrap;
  line-height: 1.5;
}

.contact-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-blue);
  text-decoration: none;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  background: rgba(0, 27, 183, 0.1);
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 27, 183, 0.2);
}

.contact-link:hover {
  background: rgba(0, 27, 183, 0.15);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 27, 183, 0.2);
}

.modal-footer {
  padding: 1.5rem 2rem;
  background: rgba(248, 250, 252, 0.8);
  border-top: 1px solid rgba(0, 27, 183, 0.1);
  display: flex;
  justify-content: center;
  align-items: center;
}

.footer-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.9rem;
  font-weight: 500;
}

/* Carte héro premium */
.rdv-hero-card {
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 2rem;
  box-shadow: 
    0 20px 40px rgba(0, 27, 183, 0.15),
    0 8px 25px rgba(0, 0, 0, 0.1);
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, 
    rgba(0, 27, 183, 0.95) 0%, 
    rgba(0, 70, 255, 0.9) 50%,
    rgba(59, 130, 246, 0.85) 100%);
}

.hero-pattern {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.08) 0%, transparent 50%),
    linear-gradient(45deg, transparent 40%, rgba(255, 255, 255, 0.03) 50%, transparent 60%);
}

.hero-content {
  position: relative;
  z-index: 1;
  padding: 2.5rem;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;
}

.hero-main {
  flex: 1;
  display: flex;
  gap: 2.5rem;
}

.hero-date-time {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.hero-date {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.date-day {
  font-size: 0.9rem;
  font-weight: 500;
  opacity: 0.9;
  text-transform: capitalize;
  margin-bottom: 0.5rem;
}

.date-number {
  font-size: 3rem;
  font-weight: 900;
  line-height: 1;
  margin-bottom: 0.25rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.date-month {
  font-size: 0.85rem;
  font-weight: 500;
  opacity: 0.8;
  text-transform: capitalize;
}

.hero-time {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.time-badge, .duration-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-weight: 600;
  font-size: 0.9rem;
}

.hero-participants {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  flex: 1;
}

.participant-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.participant-card:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.participant-avatar {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.participant-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.participant-label {
  font-size: 0.8rem;
  font-weight: 500;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.participant-name {
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.participant-contact {
  font-size: 0.85rem;
  opacity: 0.9;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.hero-status {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1rem;
}

.status-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-end;
}

.status-badge-premium {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.priority-badge-premium {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

@keyframes fadeInUp {
  from { 
    opacity: 0; 
    transform: translateY(20px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
}

.rdv-section {
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.9) 0%, 
    rgba(248, 250, 252, 0.8) 100%);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-left: 4px solid var(--primary-blue);
  transition: all 0.3s ease;
}

.rdv-section:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 27, 183, 0.1);
  border-left-color: var(--secondary-blue);
}

.rdv-section h4 {
  margin: 0 0 1.5rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--primary-blue);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-item label {
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-item span {
  color: #1f2937;
  font-weight: 500;
  padding: 0.5rem 0;
}

.phone-link, .email-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-blue);
  text-decoration: none;
  font-weight: 600;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  background: rgba(0, 27, 183, 0.1);
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 27, 183, 0.2);
}

.phone-link:hover, .email-link:hover {
  background: rgba(0, 27, 183, 0.15);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.2);
}

.notes-content {
  background: rgba(249, 250, 251, 0.8);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid rgba(226, 232, 240, 0.8);
  font-style: italic;
  color: #6b7280;
  line-height: 1.6;
}

.materials-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.material-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background: rgba(249, 250, 251, 0.8);
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.8);
}

/* Responsive modal RDV */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 0.5rem;
  }
  
  .modal-content {
    max-height: 95vh;
    border-radius: 16px;
  }
  
  .modal-header {
    padding: 1.5rem;
    border-radius: 16px 16px 0 0;
  }
  
  .header-content h3 {
    font-size: 1.2rem;
  }
  
  .section-header {
    padding: 1rem 1.5rem 0.75rem 1.5rem;
  }
  
  .section-content {
    padding: 1.5rem;
  }
  
  .form-row {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .section-icon {
    width: 35px;
    height: 35px;
    font-size: 0.9rem;
  }
  
  .section-header h4 {
    font-size: 1rem;
  }
  
  .modal-footer {
    padding: 1rem 1.5rem;
  }
}

/* Styles pour la vue tableau */
.table-view {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 27, 183, 0.1);
  min-height: 200px;
}

.table-container {
  overflow-x: auto;
  max-height: 70vh;
  overflow-y: auto;
  position: relative;
  background: white;
}

.table-container::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

.table-container::-webkit-scrollbar-track {
  background: rgba(0, 27, 183, 0.1);
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 4px;
  transition: all 0.3s ease;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, var(--secondary-blue), var(--primary-blue));
}

.rdv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.rdv-table thead {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  position: sticky;
  top: 0;
  z-index: 10;
}

.rdv-table th {
  padding: 1rem 0.75rem;
  text-align: left;
  font-weight: 700;
  font-size: 0.85rem;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
}

.rdv-table th:last-child {
  border-right: none;
}

.rdv-table tbody tr {
  border-bottom: 1px solid rgba(0, 27, 183, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
}

.rdv-table tbody tr:hover {
  background: rgba(0, 27, 183, 0.05);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 27, 183, 0.1);
}

.rdv-table td {
  padding: 1rem 0.75rem;
  vertical-align: top;
  border-right: 1px solid rgba(0, 27, 183, 0.05);
}

.rdv-table td:last-child {
  border-right: none;
}

/* Styles des cellules */
.date-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.date-main {
  font-weight: 600;
  color: var(--primary-blue);
}

.date-day {
  font-size: 0.8rem;
  color: #6b7280;
  text-transform: capitalize;
}

.time-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.time-range {
  font-weight: 600;
  color: #1f2937;
}

.duration {
  font-size: 0.8rem;
  color: #6b7280;
  background: rgba(0, 27, 183, 0.1);
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  text-align: center;
}

.client-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.client-name {
  font-weight: 600;
  color: #1f2937;
}

.client-address {
  font-size: 0.8rem;
  color: #6b7280;
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.contact-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-blue);
  text-decoration: none;
  font-size: 0.8rem;
  transition: all 0.3s ease;
  padding: 0.25rem 0.5rem;
  border-radius: 8px;
}

.contact-link:hover {
  background: rgba(0, 27, 183, 0.1);
  transform: translateX(2px);
}

.technicien-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.technicien-name {
  font-weight: 600;
  color: #1f2937;
}

.technicien-phone {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-blue);
  text-decoration: none;
  font-size: 0.8rem;
  transition: all 0.3s ease;
}

.technicien-phone:hover {
  color: var(--secondary-blue);
}

.commercial-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.commercial-name {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #1f2937;
  font-size: 0.85rem;
}

.commercial-name i {
  color: var(--primary-blue);
  font-size: 0.8rem;
}

.commercial-unknown {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #6b7280;
  font-style: italic;
}

.commercial-unknown i {
  color: #9ca3af;
}

.intervention-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.intervention-type {
  font-weight: 600;
  color: #1f2937;
}

.intervention-device {
  font-size: 0.8rem;
  color: #6b7280;
}

.priority-badge, .status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.actions-buttons {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  padding: 0.5rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
}

.view-btn {
  background: rgba(0, 27, 183, 0.1);
  color: var(--primary-blue);
}

.view-btn:hover {
  background: var(--primary-blue);
  color: white;
  transform: scale(1.1);
}

/* États spéciaux */
.loading-row, .empty-row {
  background: rgba(249, 250, 251, 0.8);
}

.loading-cell, .empty-cell {
  text-align: center;
  padding: 3rem 1rem;
}

.loading-content, .empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: #6b7280;
}

.loading-content i {
  font-size: 2rem;
  color: var(--primary-blue);
}

.empty-content i {
  font-size: 3rem;
  color: #d1d5db;
}

.empty-content p {
  margin: 0;
  font-weight: 600;
  color: #374151;
}

.empty-content small {
  color: #9ca3af;
}

/* Responsive tableau */
@media (max-width: 1400px) {
  .table-view {
    border-radius: 12px;
  }
  
  .table-container {
    max-height: 65vh;
  }
  
  .rdv-table {
    font-size: 0.85rem;
  }
  
  .rdv-table th,
  .rdv-table td {
    padding: 0.875rem 0.625rem;
  }
  
  .rdv-table th {
    font-size: 0.8rem;
  }
}

@media (max-width: 1200px) {
  .table-container {
    max-height: 60vh;
  }
  
  .rdv-table {
    font-size: 0.8rem;
  }
  
  .rdv-table th,
  .rdv-table td {
    padding: 0.75rem 0.5rem;
  }
  
  .rdv-table th {
    font-size: 0.75rem;
  }
  
  .date-main {
    font-size: 0.85rem;
  }
  
  .date-day {
    font-size: 0.75rem;
  }
  
  .time-range {
    font-size: 0.85rem;
  }
  
  .duration {
    font-size: 0.75rem;
    padding: 0.125rem 0.375rem;
  }
  
  .client-name {
    font-size: 0.85rem;
  }
  
  .client-address {
    font-size: 0.75rem;
  }
}

@media (max-width: 900px) {
  .table-container {
    max-height: 55vh;
  }
  
  .rdv-table {
    font-size: 0.75rem;
    min-width: 800px; /* Force horizontal scroll */
  }
  
  .rdv-table th,
  .rdv-table td {
    padding: 0.625rem 0.375rem;
  }
  
  .rdv-table th {
    font-size: 0.7rem;
    padding: 0.75rem 0.375rem;
  }
  
  /* Réduire la largeur des colonnes moins importantes */
  .rdv-table th:nth-child(1), /* Date */
  .rdv-table td:nth-child(1) {
    width: 100px;
  }
  
  .rdv-table th:nth-child(2), /* Horaires */
  .rdv-table td:nth-child(2) {
    width: 90px;
  }
  
  .rdv-table th:nth-child(4), /* Contact */
  .rdv-table td:nth-child(4) {
    width: 120px;
  }
  
  .rdv-table th:nth-child(7), /* Priorité */
  .rdv-table td:nth-child(7) {
    width: 80px;
  }
  
  .rdv-table th:nth-child(8), /* Statut */
  .rdv-table td:nth-child(8) {
    width: 80px;
  }
  
  .rdv-table th:nth-child(9), /* Actions */
  .rdv-table td:nth-child(9) {
    width: 60px;
  }
}

@media (max-width: 768px) {
  .table-container {
    max-height: 50vh;
    border-radius: 12px;
  }
  
  .rdv-table {
    font-size: 0.7rem;
    min-width: 700px; /* Scroll horizontal obligatoire */
  }
  
  .rdv-table th,
  .rdv-table td {
    padding: 0.5rem 0.25rem;
  }
  
  .rdv-table th {
    font-size: 0.65rem;
    padding: 0.625rem 0.25rem;
  }
  
  /* Colonnes encore plus compactes */
  .rdv-table th:nth-child(1),
  .rdv-table td:nth-child(1) {
    width: 80px;
  }
  
  .rdv-table th:nth-child(2),
  .rdv-table td:nth-child(2) {
    width: 75px;
  }
  
  .rdv-table th:nth-child(4),
  .rdv-table td:nth-child(4) {
    width: 100px;
  }
  
  .rdv-table th:nth-child(7),
  .rdv-table td:nth-child(7) {
    width: 70px;
  }
  
  .rdv-table th:nth-child(8),
  .rdv-table td:nth-child(8) {
    width: 70px;
  }
  
  .rdv-table th:nth-child(9),
  .rdv-table td:nth-child(9) {
    width: 50px;
  }
  
  .contact-info,
  .technicien-info {
    gap: 0.25rem;
  }
  
  .contact-link,
  .technicien-phone {
    font-size: 0.65rem;
    padding: 0.125rem 0.25rem;
    gap: 0.25rem;
  }
  
  .priority-badge,
  .status-badge {
    padding: 0.25rem 0.375rem;
    font-size: 0.65rem;
  }
  
  .action-btn {
    width: 28px;
    height: 28px;
    font-size: 0.7rem;
  }
  
  .date-main {
    font-size: 0.75rem;
  }
  
  .date-day {
    font-size: 0.65rem;
  }
  
  .time-range {
    font-size: 0.75rem;
  }
  
  .duration {
    font-size: 0.65rem;
    padding: 0.125rem 0.25rem;
  }
  
  .client-name {
    font-size: 0.75rem;
  }
  
  .client-address {
    font-size: 0.65rem;
  }
  
  .technicien-name {
    font-size: 0.75rem;
  }
  
  .intervention-type {
    font-size: 0.75rem;
  }
  
  .intervention-device {
    font-size: 0.65rem;
  }
}

@media (max-width: 640px) {
  .table-container {
    max-height: 45vh;
  }
  
  .rdv-table {
    font-size: 0.65rem;
    min-width: 600px;
  }
  
  .rdv-table th,
  .rdv-table td {
    padding: 0.375rem 0.125rem;
  }
  
  .rdv-table th {
    font-size: 0.6rem;
    padding: 0.5rem 0.125rem;
  }
  
  /* Colonnes ultra-compactes */
  .rdv-table th:nth-child(1),
  .rdv-table td:nth-child(1) {
    width: 70px;
  }
  
  .rdv-table th:nth-child(2),
  .rdv-table td:nth-child(2) {
    width: 65px;
  }
  
  .rdv-table th:nth-child(4),
  .rdv-table td:nth-child(4) {
    width: 80px;
  }
  
  .rdv-table th:nth-child(7),
  .rdv-table td:nth-child(7) {
    width: 60px;
  }
  
  .rdv-table th:nth-child(8),
  .rdv-table td:nth-child(8) {
    width: 60px;
  }
  
  .rdv-table th:nth-child(9),
  .rdv-table td:nth-child(9) {
    width: 40px;
  }
  
  .action-btn {
    width: 24px;
    height: 24px;
    font-size: 0.65rem;
  }
  
  .contact-link,
  .technicien-phone {
    font-size: 0.6rem;
    padding: 0.125rem;
  }
  
  .priority-badge,
  .status-badge {
    padding: 0.125rem 0.25rem;
    font-size: 0.6rem;
  }
}

/* Styles pour la vue jour */
.day-view {
  display: flex;
  flex-direction: column;
  height: 70vh;
}

.day-header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border-radius: 16px 16px 0 0;
  margin-bottom: 1px;
}

.day-title h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  text-transform: capitalize;
}

.day-subtitle {
  margin: 0.25rem 0 0 0;
  font-size: 1rem;
  opacity: 0.9;
  font-weight: 500;
}

.day-stats {
  display: flex;
  gap: 1rem;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  backdrop-filter: blur(10px);
}

.stat-number {
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1;
}

.stat-label {
  font-size: 0.8rem;
  opacity: 0.9;
  margin-top: 0.25rem;
}

.today-btn {
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.3);
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 12px;
  color: white;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  backdrop-filter: blur(10px);
}

.today-btn:hover {
  background: rgba(255, 255, 255, 0.4);
  border-color: rgba(255, 255, 255, 0.6);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.2);
}

.today-btn i {
  font-size: 0.9rem;
}

.day-schedule {
  flex: 1;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 0 0 16px 16px;
}

.hour-slot {
  display: flex;
  min-height: 70px;
  border-bottom: 1px solid rgba(0, 27, 183, 0.1);
}

.hour-slot:last-child {
  border-bottom: none;
}

.hour-label {
  width: 100px;
  padding: 1rem 0.75rem;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.1), rgba(0, 70, 255, 0.05));
  border-right: 2px solid rgba(0, 27, 183, 0.2);
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.hour-time {
  font-weight: 700;
  color: var(--primary-blue);
  font-size: 0.9rem;
}

.hour-content {
  flex: 1;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.hour-content:hover {
  background: rgba(0, 27, 183, 0.02);
}

.day-rdv-item {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9));
  border: 1px solid rgba(0, 27, 183, 0.1);
  border-left: 4px solid var(--primary-blue);
  border-radius: 12px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 27, 183, 0.1);
}

.day-rdv-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 27, 183, 0.15);
  border-left-color: var(--secondary-blue);
}

.rdv-time-badge {
  display: inline-block;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.rdv-main-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.rdv-client-name,
.rdv-technicien-name {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
}

.rdv-client-name {
  color: #1f2937;
  font-size: 1rem;
}

.rdv-client-name i {
  color: var(--primary-blue);
}

.rdv-technicien-name {
  color: #6b7280;
  font-size: 0.9rem;
}

.rdv-technicien-name i {
  color: #9ca3af;
}

.rdv-details {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.rdv-intervention,
.rdv-location {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #6b7280;
}

.rdv-intervention i,
.rdv-location i {
  color: #9ca3af;
  width: 14px;
}

.rdv-status-priority {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.mini-status-badge,
.mini-priority-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.empty-hour-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  color: #9ca3af;
  font-style: italic;
  border: 2px dashed rgba(156, 163, 175, 0.3);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.empty-hour-slot:hover {
  border-color: var(--primary-blue);
  color: var(--primary-blue);
  background: rgba(0, 27, 183, 0.02);
}

.empty-text {
  font-size: 0.9rem;
}

/* Responsive vue jour */
@media (max-width: 768px) {
  .day-view {
    height: 60vh;
  }
  
  .day-header-container {
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
  }
  
  .day-title h3 {
    font-size: 1.2rem;
  }
  
  .day-subtitle {
    font-size: 0.9rem;
  }
  
  .hour-label {
    width: 80px;
    padding: 0.75rem 0.5rem;
  }
  
  .hour-time {
    font-size: 0.8rem;
  }
  
  .hour-content {
    padding: 0.75rem;
  }
  
  .day-rdv-item {
    padding: 0.75rem;
  }
  
  .rdv-main-info {
    gap: 0.25rem;
  }
  
  .rdv-client-name {
    font-size: 0.9rem;
  }
  
  .rdv-technicien-name {
    font-size: 0.8rem;
  }
  
  .rdv-details {
    gap: 0.5rem;
  }
  
  .rdv-intervention,
  .rdv-location {
    font-size: 0.8rem;
  }
}

/* Responsive vue semaine */
@media (max-width: 1200px) {
  .calendar-header {
    grid-template-columns: 80px repeat(7, 1fr);
  }
  
  .time-row {
    grid-template-columns: 80px repeat(7, 1fr);
  }
  
  .time-column-header,
  .time-column {
    padding: 0.75rem 0.5rem;
  }
  
  .day-header {
    padding: 0.75rem 0.5rem;
  }
  
  .day-name {
    font-size: 0.8rem;
  }
  
  .day-date {
    font-size: 1rem;
  }
  
  .time-label {
    font-size: 0.8rem;
  }
}

@media (max-width: 768px) {
  .week-view {
    height: auto;
    min-height: 400px;
    max-height: 60vh;
    overflow: hidden;
  }
  
  /* Forcer 3 colonnes sur mobile */
  .calendar-header {
    display: grid !important;
    grid-template-columns: 50px repeat(3, 1fr) !important;
    width: 100% !important;
    min-width: auto !important;
  }
  
  /* Cacher les jours 4, 5, 6, 7 dans le header */
  .calendar-header .day-header:nth-child(n+5) {
    display: none !important;
  }
  
  .time-row {
    display: grid !important;
    grid-template-columns: 50px repeat(3, 1fr) !important;
    min-height: 50px;
    width: 100% !important;
  }
  
  /* Cacher les colonnes 4, 5, 6, 7 dans les lignes */
  .time-row .day-column:nth-child(n+5) {
    display: none !important;
  }
  
  .calendar-body {
    overflow-y: auto;
    overflow-x: hidden !important;
    width: 100% !important;
    min-width: auto !important;
  }
  
  .time-column-header,
  .time-column {
    padding: 0.5rem 0.25rem;
    font-size: 0.65rem;
  }
  
  .day-header {
    padding: 0.5rem 0.25rem;
  }
  
  .day-name {
    font-size: 0.65rem;
    margin-bottom: 0.125rem;
  }
  
  .day-date {
    font-size: 0.85rem;
  }
  
  .time-label {
    font-size: 0.6rem;
  }
  
  .time-cell {
    padding: 0.25rem;
    min-height: 50px;
  }
  
  .rdv-item {
    padding: 0.25rem;
    font-size: 0.55rem;
    border-radius: 4px;
  }
  
  .rdv-client {
    font-size: 0.55rem;
    margin-bottom: 0.1rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .rdv-tech {
    font-size: 0.5rem;
    margin-bottom: 0.1rem;
  }
  
  .rdv-time {
    font-size: 0.5rem;
  }
  
  .rdv-ville {
    display: none;
  }
}

/* Responsive vue mois */
@media (max-width: 768px) {
  .month-view {
    height: 400px;
  }
  
  .day-cell {
    padding: 0.5rem;
    min-height: 50px;
  }
  
  .day-number {
    font-size: 0.8rem;
  }
  
  .rdv-dot {
    width: 6px;
    height: 6px;
  }
}

/* Responsive Header */
@media (max-width: 1400px) {
  .header {
    padding: 1.25rem 1.5rem;
  }
  
  .view-tabs {
    gap: 0.5rem;
  }
  
  .view-tabs button {
    padding: 0.75rem 1.25rem;
    font-size: 0.85rem;
  }
  
  .header-info {
    gap: 1rem;
  }
  
  .date-nav span {
    min-width: 180px;
    font-size: 1rem;
  }
  
  .day-date-picker {
    min-width: 220px;
  }
}

@media (max-width: 1200px) {
  .header {
    padding: 1rem 1.25rem;
    flex-direction: column;
    align-items: stretch;
    gap: 1.5rem;
  }
  
  .view-tabs {
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .view-tabs button {
    padding: 0.625rem 1rem;
    font-size: 0.8rem;
  }
  
  .header-info {
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1rem;
  }
  
  .rdv-count {
    padding: 0.625rem 1rem;
    font-size: 0.9rem;
  }
  
  .date-nav {
    gap: 0.75rem;
  }
  
  .date-nav span {
    min-width: 160px;
    font-size: 0.95rem;
  }
  
  .day-date-picker {
    min-width: 200px;
  }
  
  .refresh-btn {
    padding: 0.625rem 1.25rem;
    font-size: 0.85rem;
  }
}

@media (max-width: 900px) {
  .header {
    padding: 1rem;
  }
  
  .view-tabs button {
    padding: 0.5rem 0.875rem;
    font-size: 0.75rem;
  }
  
  .header-info {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
  
  .date-nav {
    flex-direction: column;
    gap: 0.75rem;
    align-items: center;
  }
  
  .date-nav span {
    min-width: auto;
    font-size: 0.9rem;
  }
  
  .day-date-picker {
    min-width: auto;
    width: 100%;
    max-width: 280px;
  }
  
  .rdv-count {
    padding: 0.5rem 0.875rem;
    font-size: 0.85rem;
  }
  
  .refresh-btn {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
    width: 100%;
    max-width: 200px;
    justify-content: center;
  }
}

@media (max-width: 640px) {
  .header {
    padding: 0.875rem;
    margin-bottom: 1.5rem;
  }
  
  .view-tabs {
    gap: 0.25rem;
  }
  
  .view-tabs button {
    padding: 0.5rem 0.75rem;
    font-size: 0.7rem;
    flex: 1;
    min-width: 0;
  }
  
  .date-nav button {
    padding: 0.375rem 0.75rem;
    font-size: 0.8rem;
  }
  
  .date-nav span {
    font-size: 0.85rem;
  }
  
  .current-day-label {
    font-size: 0.75rem;
  }
  
  .date-input {
    padding: 0.375rem 0.5rem;
    font-size: 0.8rem;
  }
}

/* Responsive pour sidebar ouverte */
@media (max-width: 1600px) {
  .planning-manager {
    padding: 1rem;
  }
}

@media (max-width: 1400px) {
  .planning-manager {
    padding: 0.875rem;
  }
  
  .header {
    border-radius: 20px;
  }
  
  .calendar-grid {
    border-radius: 20px;
    padding: 1.5rem;
  }
}

.text-muted {
  color: #6b7280;
  font-style: italic;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.text-muted i {
  color: #9ca3af;
}

/* ========== VUE CARTES MOBILE ========== */
.cards-view-mobile {
  display: none;
  padding: 0.5rem;
}

/* Forcer l'affichage sur mobile */
@media screen and (max-width: 768px) {
  .cards-view-mobile {
    display: block !important;
  }
}

.rdv-cards-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

.rdv-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 27, 183, 0.1);
  border: 1px solid rgba(0, 27, 183, 0.1);
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
}

.rdv-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 27, 183, 0.15);
}

.rdv-card-header {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.rdv-card-date {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.95rem;
}

.rdv-card-header .status-badge {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.rdv-card-body {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.rdv-card-time {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--primary-blue);
  font-size: 1rem;
}

.rdv-card-time i {
  color: var(--secondary-blue);
}

.duration-badge {
  background: rgba(0, 27, 183, 0.1);
  color: var(--primary-blue);
  padding: 0.2rem 0.5rem;
  border-radius: 8px;
  font-size: 0.75rem;
  margin-left: auto;
}

.rdv-card-client {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(0, 27, 183, 0.05);
  border-radius: 10px;
}

.rdv-card-client i {
  color: var(--primary-blue);
  margin-top: 0.2rem;
}

.client-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.rdv-card-client .client-name {
  font-weight: 600;
  color: #1f2937;
  font-size: 0.95rem;
}

.rdv-card-client .client-address {
  font-size: 0.8rem;
  color: #6b7280;
}

.rdv-card-contact {
  display: flex;
  align-items: center;
}

.contact-link-card {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-blue);
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 0.75rem;
  background: rgba(0, 27, 183, 0.1);
  border-radius: 8px;
  transition: all 0.3s ease;
  font-size: 0.9rem;
}

.contact-link-card:hover {
  background: rgba(0, 27, 183, 0.15);
}

.rdv-card-technicien,
.rdv-card-intervention {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #4b5563;
}

.rdv-card-technicien i,
.rdv-card-intervention i {
  color: #9ca3af;
  width: 16px;
}

.rdv-card-footer {
  padding: 1rem;
  background: #f8fafc;
  border-top: 1px solid rgba(0, 27, 183, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.view-details-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.view-details-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.3);
}

.loading-cards,
.empty-cards {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #6b7280;
  gap: 1rem;
}

.loading-cards i,
.empty-cards i {
  font-size: 2.5rem;
  color: var(--primary-blue);
  opacity: 0.5;
}

/* Responsive - Afficher cartes sur mobile */
@media screen and (max-width: 768px) {
  .cards-view-mobile {
    display: block !important;
    padding: 0.5rem;
  }
  
  .table-container {
    display: none !important;
  }
  
  .rdv-card-header {
    padding: 0.875rem;
  }
  
  .rdv-card-date {
    font-size: 0.9rem;
  }
  
  .rdv-card-body {
    padding: 0.875rem;
    gap: 0.625rem;
  }
  
  .rdv-card-time {
    font-size: 0.95rem;
  }
  
  .rdv-card-client {
    padding: 0.625rem;
  }
  
  .rdv-card-client .client-name {
    font-size: 0.9rem;
  }
  
  .rdv-card-footer {
    padding: 0.875rem;
  }
  
  .view-details-btn {
    padding: 0.5rem 0.875rem;
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .rdv-cards-grid {
    gap: 0.75rem;
  }
  
  .rdv-card {
    border-radius: 12px;
  }
  
  .rdv-card-header {
    padding: 0.75rem;
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
  
  .rdv-card-body {
    padding: 0.75rem;
  }
  
  .rdv-card-footer {
    padding: 0.75rem;
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .view-details-btn {
    width: 100%;
    justify-content: center;
  }
}

/* ========== RESPONSIVE CALENDRIER MOBILE ========== */
@media (max-width: 768px) {
  .planning-manager {
    padding: 0.5rem;
    overflow-x: hidden;
  }
  
  /* Header responsive */
  .header {
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
  }
  
  .view-tabs {
    width: 100%;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.25rem;
  }
  
  .view-tabs button {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
    flex: 1;
    min-width: 70px;
  }
  
  .header-info {
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
    align-items: center;
  }
  
  .date-nav {
    width: 100%;
    justify-content: center;
  }
  
  .date-nav span {
    font-size: 0.9rem;
    min-width: auto;
  }
  
  .rdv-count {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
  }
  
  .refresh-btn {
    width: 100%;
    justify-content: center;
    padding: 0.625rem 1rem;
  }
  
  /* Calendar grid responsive - IMPORTANT: pas de overflow-x */
  .calendar-grid {
    padding: 0.5rem;
    border-radius: 12px;
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
  }
  
  /* Vue Jour responsive */
  .day-view {
    height: auto;
    min-height: 50vh;
  }
  
  .day-header-container {
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem;
    border-radius: 12px 12px 0 0;
  }
  
  .day-title h3 {
    font-size: 1.1rem;
  }
  
  .day-subtitle {
    font-size: 0.85rem;
  }
  
  .day-stats {
    width: 100%;
    justify-content: space-between;
  }
  
  .stat-item {
    padding: 0.5rem 0.75rem;
  }
  
  .stat-number {
    font-size: 1.25rem;
  }
  
  .today-btn {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
  }
  
  .hour-slot {
    min-height: 60px;
  }
  
  .hour-label {
    width: 60px;
    padding: 0.5rem 0.25rem;
  }
  
  .hour-time {
    font-size: 0.75rem;
  }
  
  .hour-content {
    padding: 0.5rem;
  }
  
  .day-rdv-item {
    padding: 0.75rem;
    border-radius: 8px;
  }
  
  .rdv-time-badge {
    font-size: 0.7rem;
    padding: 0.2rem 0.5rem;
  }
  
  .rdv-client-name {
    font-size: 0.85rem;
  }
  
  .rdv-technicien-name {
    font-size: 0.75rem;
  }
  
  .rdv-details {
    gap: 0.5rem;
  }
  
  .rdv-intervention,
  .rdv-location {
    font-size: 0.75rem;
  }
  
  .mini-status-badge,
  .mini-priority-badge {
    font-size: 0.6rem;
    padding: 0.2rem 0.4rem;
  }
  
  .empty-hour-slot {
    min-height: 40px;
  }
  
  .empty-text {
    font-size: 0.8rem;
  }
  
  /* Vue Semaine responsive - Afficher seulement 3 jours sur mobile */
  .week-view {
    display: flex !important;
    flex-direction: column;
    height: auto !important;
    min-height: 350px;
    max-height: 55vh;
    overflow: hidden !important;
    width: 100% !important;
  }
  
  .month-view {
    display: block !important;
    height: auto;
    min-height: 300px;
  }
  
  /* Adaptation de la vue Semaine pour mobile - 3 jours visibles */
  .week-view .calendar-header {
    display: grid !important;
    grid-template-columns: 50px repeat(3, 1fr) !important;
    min-width: auto !important;
    width: 100% !important;
    gap: 0;
    padding: 0;
  }
  
  .week-view .time-column-header {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem;
    font-size: 0.6rem;
  }
  
  .week-view .day-header {
    padding: 0.4rem 0.2rem;
    border-right: 1px solid rgba(255, 255, 255, 0.2);
  }
  
  /* Cacher les jours après les 3 premiers sur mobile (4ème enfant = 5ème colonne car time-column-header est le 1er) */
  .week-view .calendar-header .day-header:nth-child(n+5) {
    display: none !important;
  }
  
  .week-view .day-name {
    font-size: 0.65rem;
  }
  
  .week-view .day-date {
    font-size: 0.85rem;
  }
  
  .week-view .calendar-body {
    max-height: 45vh;
    overflow-y: auto;
    overflow-x: hidden !important;
    width: 100% !important;
    min-width: auto !important;
  }
  
  .week-view .time-row {
    display: grid !important;
    grid-template-columns: 50px repeat(3, 1fr) !important;
    min-height: 45px;
    width: 100% !important;
  }
  
  /* Cacher les colonnes après les 3 premiers jours (day-column) */
  .week-view .time-row .day-column:nth-child(n+5) {
    display: none !important;
  }
  
  .week-view .time-column {
    padding: 0.4rem 0.2rem;
  }
  
  .week-view .time-label {
    font-size: 0.55rem;
  }
  
  .week-view .time-cell {
    min-height: 45px;
    padding: 0.2rem;
  }
  
  .week-view .rdv-item {
    font-size: 0.5rem;
    padding: 0.15rem;
    border-radius: 3px;
  }
  
  .week-view .rdv-client,
  .week-view .rdv-tech,
  .week-view .rdv-time {
    font-size: 0.5rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .week-view .rdv-ville {
    display: none !important;
  }
  
  .week-view .rdv-item .rdv-time {
    font-size: 0.55rem;
  }
  
  .week-view .rdv-item .rdv-client {
    font-size: 0.6rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .week-view .rdv-client,
  .week-view .rdv-tech,
  .week-view .rdv-ville,
  .week-view .rdv-time {
    font-size: 0.6rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  /* Adaptation de la vue Mois pour mobile */
  .month-view .month-header {
    border-radius: 12px 12px 0 0;
  }
  
  .month-view .month-day-header {
    padding: 0.5rem 0.25rem;
    font-size: 0.7rem;
  }
  
  .month-view .month-grid {
    grid-template-columns: repeat(7, 1fr);
    gap: 1px;
    border-radius: 0 0 12px 12px;
  }
  
  .month-view .day-cell {
    padding: 0.25rem;
    min-height: 50px;
  }
  
  .month-view .day-number {
    font-size: 0.75rem;
  }
  
  .month-view .day-cell.today .day-number {
    padding: 0.15rem 0.35rem;
    font-size: 0.7rem;
  }
  
  .month-view .rdv-dot {
    width: 6px;
    height: 6px;
  }
  
  .month-view .more-rdvs {
    font-size: 0.6rem;
    padding: 0.05rem 0.2rem;
  }
  
  .month-day-details {
    margin-top: 1rem;
    padding: 1rem;
  }
  
  .month-day-details h4 {
    font-size: 0.95rem;
  }
  
  .month-rdv-item {
    padding: 0.5rem 0.75rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  .month-rdv-item .rdv-time {
    font-size: 0.8rem;
    min-width: 80px;
  }
  
  .month-rdv-item .rdv-client {
    font-size: 0.85rem;
  }
  
  .month-rdv-item .rdv-tech {
    font-size: 0.75rem;
  }
}

@media (max-width: 480px) {
  .planning-manager {
    padding: 0.25rem;
  }
  
  .header {
    padding: 0.75rem;
    border-radius: 12px;
  }
  
  .view-tabs button {
    padding: 0.4rem 0.5rem;
    font-size: 0.75rem;
  }
  
  .calendar-grid {
    padding: 0.5rem;
  }
  
  .day-header-container {
    padding: 0.75rem;
  }
  
  .day-title h3 {
    font-size: 1rem;
  }
  
  .hour-label {
    width: 50px;
  }
  
  .hour-time {
    font-size: 0.7rem;
  }
  
  .day-rdv-item {
    padding: 0.5rem;
  }
  
  .rdv-main-info {
    gap: 0.25rem;
  }
}
</style>
